import type { GAItem } from '../types';
import type { CatalogueModel } from 'api-types';

type Options = {
  listId?: string;
  listName?: string;
  listType?: string;
  index?: number;
};

/**
 * Maps a product variant from the catalogue model to a Google Analytics item.
 * 
 * @param product - The product variant from the catalogue model
 * @param options - Additional options for the GA item (list info, index, etc.)
 * @returns GAItem - Formatted item for Google Analytics 4 tracking
 */
export function productToGAItem(product: CatalogueModel.ProductVariant, options: Options = {}): GAItem {
  const gaItem: GAItem = {
    item_id: product.key || product.id?.toString() || '',
    item_name: product.name || '',
  };

  const currency = product.price?.displayRawValue?.split(" ")[1];
  if (currency) {
    gaItem.currency = currency.toUpperCase();
  }

  // Only add optional fields if they have actual values
  if (product.price?.rawValue !== undefined) {
    gaItem.price = product.price.rawValue;
  }
  // Note: PriceTemplate doesn't have currency field, would need to get it from basket context
  if (product.brandName) {
    gaItem.item_brand = product.brandName.toLowerCase();
  }
  if (product.id) {
    gaItem.item_variant = product.id.toString();
  }
  if (product.categories?.[0]?.name) {
    gaItem.item_category = product.categories[0].name.toLowerCase();
  }
  if (product.categories?.[1]?.name) {
    gaItem.item_category2 = product.categories[1].name.toLowerCase();
  }
  if (product.categories?.[2]?.name) {
    gaItem.item_category3 = product.categories[2].name.toLowerCase();
  }
  if (options?.index !== undefined) {
    gaItem.index = options.index;
  }
  if (options?.listId) {
    gaItem.item_list_id = options.listId;
  }
  if (options?.listName) {
    gaItem.item_list_name = options.listName;
  }
  if (options?.listType) {
    gaItem.item_list_type = options.listType;
  }

  return gaItem;
}
